import type { StudioPathLike } from '@sanity/client/csm';
import type { CreateDataAttribute, CreateDataAttributeProps } from '@sanity/visual-editing';
import type { PropsWithChildren} from 'react';
import { createContext, useContext } from 'react';

export type DataSanity<T = { id: string; type: string }> = CreateDataAttribute<CreateDataAttributeProps & T> & { _id: string; path: StudioPathLike };

const DataAttributeContext = createContext<undefined | DataSanity>(undefined);

export default function useDataAttribute() {
    return useContext(DataAttributeContext);
}

export function ProvideDataAttribute({ value, children }: PropsWithChildren<{ value: DataSanity }>) {
    return <DataAttributeContext.Provider value={value}>{children}</DataAttributeContext.Provider>;
}

type ScopeProps =
    | {
          path: StudioPathLike;
          _id?: never;
          _type?: never;
          _key?: never;
      }
    | {
          path?: never;
          _id: string;
          _type: string;
          _key?: never;
      }
    | {
          path?: never;
          _id?: never;
          _type?: never;
          _key: string;
      };

export function ScopeDataAttribute({ children, ...props }: PropsWithChildren<ScopeProps>) {
    const dataAttribute = useDataAttribute();

    if (!dataAttribute) return children;

    if (typeof props._id === 'string') {
        return <DataAttributeContext.Provider value={reset(dataAttribute, props._id, props._type)}>{children}</DataAttributeContext.Provider>;
    } else if (typeof props._key === 'string') {
        return <DataAttributeContext.Provider value={scope(dataAttribute, [props])}>{children}</DataAttributeContext.Provider>;
    } else {
        return <DataAttributeContext.Provider value={scope(dataAttribute, props.path)}>{children}</DataAttributeContext.Provider>;
    }
}

function scope(dataAttribute: DataSanity, path: StudioPathLike) {
    const value = dataAttribute.scope(path) as DataSanity;
    value._id = dataAttribute._id;
    value.path = [...(Array.isArray(dataAttribute.path) ? dataAttribute.path : [dataAttribute.path]), ...(Array.isArray(path) ? path : [path])];
    return value;
}

function reset(dataAttribute: DataSanity, id: string, type: string) {
    const value = dataAttribute.combine({ id, type, path: [] }) as DataSanity;
    value._id = id;
    value.path = [];
    return value;
}
